/**
 * Typed localStorage read with validation and a fallback. Returns `fallback`
 * when storage is unavailable (SSR, private browsing), the key is unset, the
 * value isn't valid JSON, or `isValid` rejects the parsed value.
 */
export const getStoredValue = <T>(
  key: string,
  isValid: (value: unknown) => value is T,
  fallback: T,
): T => {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return fallback;
    const parsed = JSON.parse(raw);
    return isValid(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
};

/** Typed localStorage write. Silently no-ops if storage is unavailable. */
export const storeValue = <T>(key: string, value: T): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore — the value just won't persist this session.
  }
};
